import { provideHttpClient, withXhr } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  isDevMode,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideCalendarI18n } from '@aurora/lib';
import { HealthService } from '@aurora/modules/health';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { provideSpartanHlm } from '@spartan-ng/helm/utils';
import { environment } from 'environments/environment';
import { forkJoin, lastValueFrom } from 'rxjs';
import { routes } from './app.routes';
import { applyBrand } from './apply-brand';
import { provideAurora } from './aurora.provider';
import { buildTranslocoLangConfig } from './transloco-lang.factory';
import { TranslocoHttpLoader } from './transloco-loader';

const translocoLangConfig = buildTranslocoLangConfig(environment.lang);

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideSpartanHlm(),
    provideCalendarI18n(environment.calendar.weekStartsOn),
    provideRouter(routes),
    provideHttpClient(withXhr()),
    provideTransloco({
      config: {
        availableLangs: translocoLangConfig.availableLangs,
        defaultLang: translocoLangConfig.defaultLang,
        fallbackLang: translocoLangConfig.fallbackLang,
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        scopes: { keepCasing: true },
      },
      loader: TranslocoHttpLoader,
    }),

    // Eagerly load the default language and navigation scope at app startup
    provideAppInitializer(() => {
      const transloco = inject(TranslocoService);
      const lang = transloco.getDefaultLang();
      return lastValueFrom(forkJoin([transloco.load(lang), transloco.load(`navigation/${lang}`)]));
    }),

    // Fetch health data (version + status) from the backend at startup.
    // The endpoint is public — no auth gate needed.
    provideAppInitializer(() => inject(HealthService).fetch()),

    // Brand: browser tab title and favicon from `environment.appearance.brand`.
    provideAppInitializer(applyBrand),

    /* #region customizations */
    provideAurora(),
    /* #endregion customizations */
  ],
};
